import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { opsApi } from "../../lib/api";

const authed = async (url) => {
  const r = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } });
  if (!r.ok) throw new Error("page");
  return URL.createObjectURL(await r.blob());
};

/** SUB-2 §1.5 — side-by-side viewer: original page | returned page, changed regions outlined on
 *  the returned side. Click a region in the Review panel to land on its page. */
export default function SideBySideViewer({ project, rev, review, page: initial = 0, focusId = "", onClose }) {
  const [page, setPage] = useState(initial);
  const [imgs, setImgs] = useState({});
  const [loading, setLoading] = useState(true);
  const count = review.page_count?.returned || 1;
  const map = (review.pages || []).find((p) => p.returned === page);
  const origPage = map ? map.original : null;
  const size = review.page_sizes?.returned?.[page] || [612, 792];
  const regions = (review.findings || []).filter((f) => f.page_returned === page);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const jobs = [authed(opsApi.submittalReviewPageUrl(project.id, rev.id, "returned", page)).catch(() => "")];
    jobs.push(origPage == null ? Promise.resolve("") : authed(opsApi.submittalReviewPageUrl(project.id, rev.id, "original", origPage)).catch(() => ""));
    Promise.all(jobs).then(([r, o]) => { if (alive) { setImgs({ r, o }); setLoading(false); } });
    return () => { alive = false; };
  }, [page, origPage, project.id, rev.id]);

  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") setPage((p) => Math.min(p + 1, count - 1)); if (e.key === "ArrowLeft") setPage((p) => Math.max(p - 1, 0)); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [count, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-900/90 p-4 text-white" data-testid="ops-review-viewer">
      <div className="flex items-center gap-3 text-sm">
        <span className="font-semibold">{rev.number}{rev.revision ? ` Rev ${rev.revision}` : ""} — side by side</span>
        <span className="text-slate-300">returned p.{page + 1} / {count}{origPage != null ? ` ↔ original p.${origPage + 1}` : " · no matching original page (added sheet)"}</span>
        <button onClick={() => setPage((p) => Math.max(p - 1, 0))} className="rounded-full bg-white/10 p-1 hover:bg-white/20" data-testid="ops-review-viewer-prev"><ChevronLeft className="h-4 w-4" /></button>
        <button onClick={() => setPage((p) => Math.min(p + 1, count - 1))} className="rounded-full bg-white/10 p-1 hover:bg-white/20" data-testid="ops-review-viewer-next"><ChevronRight className="h-4 w-4" /></button>
        <span className="ml-auto text-xs text-slate-300">{regions.length} region(s) on this page</span>
        <button onClick={onClose} className="rounded-full bg-white/10 p-1 hover:bg-white/20" data-testid="ops-review-viewer-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-3 grid flex-1 gap-3 overflow-auto md:grid-cols-2">
        {[["Original", imgs.o, false], ["Returned", imgs.r, true]].map(([label, src, overlay]) => (
          <div key={label} className="relative min-h-0 overflow-auto rounded-lg bg-white/5 p-2">
            <p className="mb-1 text-xs uppercase tracking-wide text-slate-300">{label}</p>
            {loading ? <div className="flex items-center gap-2 py-10 text-xs"><Loader2 className="h-4 w-4 animate-spin" /> Rendering…</div>
              : src ? (
                <div className="relative inline-block">
                  <img src={src} alt={label} className="max-w-full rounded bg-white" />
                  {overlay && regions.map((f) => (
                    <div key={f.id} title={`${f.label}: ${f.text || ""}`}
                         className={`absolute rounded-sm border-2 ${f.id === focusId ? "border-amber-400 bg-amber-300/20" : "border-rose-500 bg-rose-400/10"}`}
                         style={{ left: `${(f.bbox[0] / size[0]) * 100}%`, top: `${(f.bbox[1] / size[1]) * 100}%`,
                                  width: `${((f.bbox[2] - f.bbox[0]) / size[0]) * 100}%`, height: `${((f.bbox[3] - f.bbox[1]) / size[1]) * 100}%` }}
                         data-testid={`ops-review-region-${f.id}`} />
                  ))}
                </div>
              ) : <p className="py-10 text-center text-xs text-slate-400">No page to show.</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
